import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Palette, Tag } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../ui/dialog';
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '../ui/form';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  Category,
  CategoryFormData,
  CategoryType,
  formatCategoryType
} from '../../types/category';
import { useCategoryMutations } from '../../hooks/useCategoryMutations';

const formSchema = z.object({
  name: z.string()
    .min(2, 'Nome deve ter pelo menos 2 caracteres')
    .max(50, 'Nome deve ter no máximo 50 caracteres'),
  description: z.string().optional(),
  type: z.nativeEnum(CategoryType, {
    required_error: 'Selecione um tipo'
  }),
  color: z.string().optional()
});

interface CategoryFormProps {
  category?: Category | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const colorOptions = [
  '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#6366f1',
  '#64748b', '#dc2626', '#059669', '#7c3aed', '#db2777'
];

export function CategoryForm({ category, open, onOpenChange, onSuccess }: CategoryFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!category;
  const { createCategory, updateCategory, loading: mutationLoading } = useCategoryMutations();

  const form = useForm<CategoryFormData>({
    // resolver: zodResolver(formSchema), // Temporarily disabled for build
    defaultValues: {
      name: '',
      description: '',
      type: CategoryType.EXPENSE,
      color: colorOptions[0]
    }
  });

  const watchedName = form.watch('name');
  const watchedColor = form.watch('color');

  // Update form when category changes
  useEffect(() => {
    if (category) {
      form.reset({
        name: category.name,
        description: category.description || '',
        type: category.type,
        color: category.color || colorOptions[0]
      });
    } else {
      form.reset({
        name: '',
        description: '',
        type: CategoryType.EXPENSE,
        color: colorOptions[0]
      });
    }
  }, [category, form]);

  const handleSubmit = async (data: CategoryFormData) => {
    setIsSubmitting(true);
    try {
      let result;

      if (isEditing && category) {
        result = await updateCategory(category.id, data);
      } else {
        result = await createCategory(data);
      }

      if (result.data) {
        if (!isEditing) {
          form.reset();
        }
        onOpenChange(false);
        // Force refresh to ensure UI updates
        setTimeout(() => {
          onSuccess?.();
        }, 100);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            {isEditing ? 'Editar Categoria' : 'Nova Categoria'}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'Edite as informações da categoria' 
              : 'Crie uma nova categoria para organizar suas transações'
            }
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Preview */}
            {watchedName && (
              <div className="p-3 rounded-lg bg-muted/50 border border-dashed">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback 
                      style={{ 
                        backgroundColor: watchedColor || colorOptions[0],
                        color: 'white'
                      }}
                    >
                      {watchedName.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium">{watchedName}</div>
                    <div className="text-sm text-muted-foreground">Prévia da categoria</div>
                  </div>
                </div>
              </div>
            )}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome da Categoria *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Ex: Marketing, Vendas, Salários..."
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Nome único para identificar a categoria
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo *</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={CategoryType.INCOME}>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                          {formatCategoryType(CategoryType.INCOME)}
                        </div>
                      </SelectItem>
                      <SelectItem value={CategoryType.EXPENSE}>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-red-500" />
                          {formatCategoryType(CategoryType.EXPENSE)}
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Define se a categoria é para receitas ou despesas
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    Cor
                  </FormLabel>
                  <FormControl>
                    <div className="grid grid-cols-5 gap-2">
                      {colorOptions.map((color) => (
                        <Button
                          key={color}
                          type="button"
                          variant="outline"
                          className={`w-10 h-10 p-0 border-2 transition-all ${
                            field.value === color 
                              ? 'border-foreground scale-110' 
                              : 'border-muted hover:border-border'
                          }`}
                          style={{ backgroundColor: color }}
                          onClick={() => field.onChange(color)}
                        >
                          {field.value === color && (
                            <div className="w-2 h-2 rounded-full bg-card shadow-lg" />
                          )}
                        </Button>
                      ))}
                    </div>
                  </FormControl>
                  <FormDescription>
                    Escolha uma cor para identificar visualmente a categoria
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Descreva brevemente o uso desta categoria..."
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Descrição opcional para esclarecer o uso da categoria
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

  
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || mutationLoading}
                className="gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Tag className="h-4 w-4" />
                    {isEditing ? 'Salvar Alterações' : 'Criar Categoria'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}